import React, { useEffect, useState } from 'react';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { Button, notification, Radio, Form, Col, Row } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  storeCategory,
  showCategory,
  editCategory,
  updateCategory,
  createCategory,
} from '@app/api/post-categories.api'; // Updated import based on PostCategory API
import StatusBar from '@app/components/statusBar/statusBar';
import { notificationController } from '@app/controllers/notificationController';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useTranslation } from 'react-i18next';

interface PostCategoryFormProps {
  pageTitle: string;
  editable: boolean;
  id: string | null;
  type: 'edit' | 'create' | 'view';
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

const PostCategoryForm: React.FC<PostCategoryFormProps> = ({
  editable = false,
  id,
  pageTitle,
  type,
  setCurrentBreadcrumName,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const [status, setStatus] = useState<string | null>(null);
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const [parentIdOptions, setParentIdOptions] = useState<{ value: number; label: string }[]>([]);
  const navigate = useNavigate();
  const categoryId = parseInt(id ?? '0');

  const setApiData = (res: any) => {
    setCurrentBreadcrumName(res?.post_category?.name || 'New');
    form.setFieldsValue({
      name: res?.post_category?.name,
      slug: res?.post_category?.slug,
      description: res?.post_category?.description,
      status: res?.post_category?.status,
      parent_id: res?.post_category?.parent_id,
      parent_name: res?.post_category?.parent?.name ?? 'None',
    });
    setStatus(res.post_category.status);
  };

  const setOptions = (res: any) => {
    const mappedStatuses = res.status_options.map((status: any) => ({
      label: status.label,
      value: status.value,
    }));
    setStatusOptions(mappedStatuses);

    const parentCategoryOptions = res.root_options?.map((category: { value: number; label: string }) => ({
      value: category.value,
      label: category.label,
    }));
    setParentIdOptions(parentCategoryOptions || []);
  };

  const getCategory = () => {
    setLoading(true);
    if (categoryId) {
      if (type === 'view') {
        showCategory(categoryId)
          .then((res: any) => {
            setApiData(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to get post category', description: err.message });
          });
      } else if (type === 'edit') {
        editCategory(categoryId)
          .then((res: any) => {
            setApiData(res);
            setOptions(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to edit post category', description: err.message });
          });
      }
    } else if (type === 'create') {
      createCategory()
        .then((res: any) => {
          setOptions(res);
          setLoading(false);
        })
        .catch((err: any) => {
          setLoading(false);
          notification.error({ message: 'Failed to fetch post categories', description: err.message });
        });
    }
  };

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" loading={isLoading} onClick={() => form.submit()} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/post-categories')} style={{ marginRight: '8px' }} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton type="primary" onClick={() => navigate(`/edit-post-category/${id}`)} size="small">
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, isLoading, editable, id, addHeaderButton, t, navigate, removeHeaderButton]);

  useEffect(() => {
    getCategory();
  }, [id, setCurrentBreadcrumName]);

  const handleSubmit = (values: any) => {
    if (editable) {
      const submissionData = {
        ...values,
      };

      if (type === 'create') {
        storeCategory(submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate('/view-post-category', { state: { id: res?.data?.id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: err.message });
            setLoading(false);
          });
      } else if (type === 'edit' && categoryId) {
        updateCategory(categoryId, submissionData)
          .then(() => {
            setLoading(false);
            navigate('/view-post-category', { state: { id: categoryId } });
          })
          .catch((err: any) => {
            notificationController.error({ message: err.message });
            setLoading(false);
          });
      }
    }
  };

  return (
    <BaseRow gutter={[16, 16]} style={{ margin: '0 8px' }}>
      {' '}
      {/* Add small margin around the full page */}
      <BaseCol span={24}>
        <StatusBar id={categoryId} status={status} />
      </BaseCol>
      <BaseCol span={24}>
        <BaseCard id="postCategoryForm" title={pageTitle} padding="1.25rem">
          <BaseRow gutter={[16, 16]}>
            <BaseCol span={24}>
              <Form
                form={form}
                name="postCategoryForm"
                labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
                labelAlign="left"
                labelWrap
                wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
                size="small"
                onFinish={handleSubmit}
                style={{ marginLeft: '40px' }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                      <BaseInput placeholder="Enter Name" readOnly={!editable} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={24} lg={12}>
                    <Form.Item name="slug" label="Slug" rules={[{ required: true, message: 'Slug is required' }]}>
                      <BaseInput placeholder="Enter Slug" readOnly={!editable} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item label="Status" name="status" rules={[{ required: true, message: 'Status is required' }]}>
                      {!editable ? (
                        <BaseInput
                          readOnly
                          value={
                            statusOptions.find((option) => option.value === form.getFieldValue('status'))?.label || ''
                          }
                        />
                      ) : (
                        <BaseSelect placeholder="Select Status" disabled={!editable} options={statusOptions} />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item label="Description" name="description" rules={[{ required: false }]}>
                      {!editable ? (
                        <BaseInput readOnly value={form.getFieldValue('description') || ''} />
                      ) : (
                        <BaseInput.TextArea placeholder="Enter Description" disabled={!editable} />
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item label="Parent Category">
                      {!editable ? (
                        <BaseInput readOnly value={form.getFieldValue('parent_name') || ''} />
                      ) : (
                        <Form.Item name="parent_id" noStyle>
                          <BaseSelect
                            placeholder="Select Parent Category"
                            options={parentIdOptions}
                            disabled={!editable}
                          />
                        </Form.Item>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </BaseCol>
          </BaseRow>
        </BaseCard>
      </BaseCol>
    </BaseRow>
  );
};

export default PostCategoryForm;
